import classNames from 'classnames';
import type React from 'react';
import Button from '../../../components/Button';
import Link from '../../../components/Link';
import PillBox from '../../../components/PillBox';
import Tooltip from '../../../components/Tooltip';
import { getWithCount } from '../../../services/Pluralize';
import styles from './style.m.less';

const specialStyleClassNames = {
    'coming-soon': styles.styleComingSoon,
};

export type SpecialNavItemStyles = keyof typeof specialStyleClassNames;

type BaseProps = {
    compact?: boolean;
    disabled?: boolean;
    icon: React.ReactNode;
    label: string;
    notificationCount?: number;
    specialStyle?: SpecialNavItemStyles;
};

type LinkProps = BaseProps & React.ComponentProps<typeof Link> & { current?: boolean };

export function NavItemLink({
    icon,
    label,
    compact,
    className,
    disabled,
    current,
    notificationCount,
    specialStyle,
    ...linkProps
}: LinkProps) {
    return (
        <CompactTooltipWrapper label={label} compact={compact}>
            <Link
                hasTextStyle={false}
                {...linkProps}
                className={classNames(
                    className,
                    styles.item,
                    {
                        [styles.current]: current,
                        [styles.disabled]: disabled,
                    },
                    specialStyle && specialStyleClassNames[specialStyle]
                )}
                aria-disabled={disabled}
                aria-current={current}
            >
                <Content icon={icon} label={label} compact={compact} notificationCount={notificationCount} />
            </Link>
        </CompactTooltipWrapper>
    );
}

type ButtonProps = BaseProps & Omit<React.ComponentProps<typeof Button>, 'kind'>;

export function NavItemButton({
    icon,
    label,
    compact,
    className,
    notificationCount,
    specialStyle,
    ...buttonProps
}: ButtonProps) {
    return (
        <CompactTooltipWrapper label={label} compact={compact}>
            <Button
                {...buttonProps}
                kind="PLAIN"
                className={classNames(className, styles.item, specialStyle && specialStyleClassNames[specialStyle])}
            >
                <Content icon={icon} label={label} compact={compact} notificationCount={notificationCount} />
            </Button>
        </CompactTooltipWrapper>
    );
}

function Content({
    icon,
    label,
    compact,
    notificationCount,
}: Pick<BaseProps, 'icon' | 'label' | 'compact' | 'notificationCount'>) {
    return (
        <>
            {icon} {!compact && <span className={styles.label}>{label}</span>}{' '}
            <NotificationIndicator notificationCount={notificationCount} compact={compact} />
        </>
    );
}

type TooltipWrapperProps = {
    children: React.ReactNode;
    label: string;
    compact?: boolean;
};

function CompactTooltipWrapper({ children, label, compact }: TooltipWrapperProps) {
    if (compact) {
        // IMPORTANT: Check if this is ok from an a11y perspective.
        // We might need to make sure the interactive element is directly labeled by the tooltip
        return (
            <Tooltip tooltip={label} position="right" instant>
                {children}
            </Tooltip>
        );
    }
    return <>{children}</>;
}

function NotificationIndicator({ notificationCount, compact }: Pick<BaseProps, 'notificationCount' | 'compact'>) {
    if (!notificationCount) {
        return null;
    }

    return (
        <span aria-label={getWithCount('Notification', notificationCount)}>
            {compact ? <span className={styles.notificationDot} /> : <PillBox color="RED" value={notificationCount} />}
        </span>
    );
}
